/** 数字与单位的展示格式化。全部为纯函数，便于单测。 */

/** 四舍五入到指定小数位，并消除 -0。 */
export function round(value: number, digits = 1): number {
  const factor = 10 ** digits;
  const rounded = Math.round(value * factor) / factor;
  return Object.is(rounded, -0) ? 0 : rounded;
}

/** 千分位分组，整数部分每三位一个逗号。 */
function group(intPart: string): string {
  const negative = intPart.startsWith('-');
  const digits = negative ? intPart.slice(1) : intPart;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return negative ? `-${grouped}` : grouped;
}

/** 固定小数位 + 千分位。`null` / 非有限数一律返回破折号。 */
export function formatNumber(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const fixed = Math.abs(value).toFixed(digits);
  const [intPart = '0', fracPart] = fixed.split('.');
  const sign = value < 0 ? '-' : '';
  return fracPart ? `${sign}${group(intPart)}.${fracPart}` : `${sign}${group(intPart)}`;
}

/** 紧凑数值：整数不带小数位，非整数保留一位。 */
export function formatCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return Number.isInteger(value) ? formatNumber(value, 0) : formatNumber(value, 1);
}

export function formatCurrency(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `¥${formatNumber(value, digits)}`;
}

/** 克 → 展示用数值 + 单位（≥1000 g 时换算为 kg）。 */
export function formatMass(grams: number | null | undefined): { value: string; unit: string } {
  if (grams === null || grams === undefined || !Number.isFinite(grams)) {
    return { value: '—', unit: 'g' };
  }
  if (Math.abs(grams) >= 1000) {
    return { value: formatNumber(grams / 1000, 2), unit: 'kg' };
  }
  return { value: formatNumber(grams, 1), unit: 'g' };
}

export function formatPercent(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${formatNumber(value, digits)}%`;
}

/** 分钟 → 「12.4 分钟」；超过 60 分钟给「1 小时 05 分」。 */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return '—';
  if (minutes < 60) return `${formatNumber(minutes, 1)} 分钟`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes - hours * 60;
  return `${hours} 小时 ${formatNumber(rest, 0).padStart(2, '0')} 分`;
}

/** 推重比一类的无量纲比值 */
export function formatRatio(value: number | null | undefined, digits = 2): string {
  return formatNumber(value, digits);
}

/** 长度：数值 + 单位 */
export function formatLength(value: number | null | undefined, unit: string, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${formatNumber(value, digits)} ${unit}`;
}

/**
 * 表单输入 → 字段值。
 *
 * 空串返回 `undefined`（即「留空 / 未填」）；能解析成数字的返回数字；
 * 其它文本原样保留（引擎会把它当作未填，但不会抹掉用户输入的内容）。
 */
export function parseInputValue(raw: string): number | string | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : trimmed;
}

/** 字段值 → 表单初值 */
export function inputValueOf(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

/** 相对时间：刚刚 / 5 分钟前 / 3 天前 / 具体日期 */
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const time = Date.parse(iso);
  if (!Number.isFinite(time)) return '—';
  const diffMs = now.getTime() - time;
  if (diffMs < 0) return formatDate(iso);
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return '刚刚';
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} 天前`;
  return formatDate(iso);
}

export function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
